import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import MyTimeSheet from "./MyTimeSheet";
import styled from "styled-components";
import { useSelector } from "react-redux";
import {
  getMenuList,
  getUserInfo,
  getUserList,
  getUserRoleId,
} from "redux/selectors";
import TimeSheetHistory from "./History";
import {
  Breadcrumb,
  ConfigProvider,
  DatePicker,
  Form,
  Modal,
  notification,
  Spin,
} from "antd";
import en_GB from "antd/lib/locale-provider/en_GB";
import "moment/locale/en-gb";
import moment from "moment";
import Button from "atoms/Button";
import { generateNiceWeekFromWeek, generateTimesheetWeek } from "./useWeekData";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useLocation } from "react-router-dom";
import COLORS from "common/Colors";
import { findWeekId } from "utils/Date";

moment.locale("en-gb");

const TimeSheet = () => {
  const [showTimesheet, setShowTimesheet] = useState(false);
  const [selectedTimesheetPeriod, setSelectedTimesheetPeriod] = useState(null);
  const [showAddTimeSheet, setShowAddTimeSheet] = useState(false);
  const [duplicateTimesheet, setDuplicateTimesheet] = useState(false);
  const location = useLocation();

  const [selectedWeek, setSelectedWeek] = useState(null);
  const [duplicateWeek, setDuplicateWeek] = useState(null);

  const [form] = Form.useForm();

  const breadcrumbRef = useRef(null);
  const [breadcrumbHeight, setBreadcrumbHeight] = useState(17);

  useLayoutEffect(() => {
    const el = breadcrumbRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const updateHeight = () => setBreadcrumbHeight(el.offsetHeight);
    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showTimesheet]);

  const cancelAddTimeSheet = () => {
    setShowAddTimeSheet(false);
  };

  // useEffect(() => {
  //   if (location.state?.period) {
  //     setSelectedTimesheetPeriod(location.state.period);
  //     setDuplicateTimesheet(location.state.duplicateSheet || false);
  //     setShowTimesheet(true);
  //   }
  // }, [location.state]);

  const handleClickTimeSheet = (period, duplicateSheet = false) => {
    setSelectedTimesheetPeriod(period);
    setDuplicateTimesheet(duplicateSheet);
    setShowTimesheet(true);
    setShowAddTimeSheet(false);
    form.resetFields();
  };

  const handleBackToList = () => {
    setShowTimesheet(false);
    setSelectedTimesheetPeriod(null);
    setDuplicateTimesheet(false);
  };

  const userRoleId = useSelector(getUserRoleId);
  const menuList = useSelector(getMenuList);

  let Menulist = menuList[userRoleId] || Object.values(menuList)[0] || menuList;

  Menulist = Array.isArray(Menulist) ? Menulist : [];

  const haveMyTimeSheetAccess = Menulist.find(
    (item) => item.key === "mytimesheet"
  )?.access;

  const havePendingApprovalAccess = Menulist.find(
    (item) => item.key === "pendingapprovalstimesheet"
  )?.access;

  const haveDeclinedAccess = Menulist.find(
    (item) => item.key === "declinedtimesheet"
  )?.access;

  const haveTimesheetAccess =
    haveMyTimeSheetAccess || havePendingApprovalAccess || haveDeclinedAccess;

  useEffect(() => {
    if (location.state?.period) {
      setSelectedTimesheetPeriod(location.state.period);
      setDuplicateTimesheet(location.state.duplicateSheet || false);
      setShowTimesheet(true);
      return;
    }

    if (location.state?.activeTab) {
      setShowTimesheet(true);
    }
  }, [
    location.state?.activeTab,
    location.state?.period,
    location.state?.navigatedAt,
  ]);

  return (
    <TimeSheetWrapper
      style={{ "--timesheet-breadcrumb-h": `${breadcrumbHeight}px` }}
    >
      {showTimesheet ? (
        <div>
          <div className="timesheet-sticky-breadcrumb" ref={breadcrumbRef}>
            <Breadcrumb separator=">">
              <Breadcrumb.Item onClick={handleBackToList}>
                Timesheet
              </Breadcrumb.Item>
              <Breadcrumb.Item>Adding New Timesheet</Breadcrumb.Item>
            </Breadcrumb>
          </div>

          {haveTimesheetAccess && (
            <MyTimeSheet
              selectedPeriod={selectedTimesheetPeriod}
              duplicateTimesheet={duplicateTimesheet}
              onCancel={handleBackToList}
            />
          )}
        </div>
      ) : (
        <div>
          <div className="heading">
            <span>Timesheet</span>
            <span>
              <Button
                className="btn-timesheet"
                style={{ background: COLORS.PRIMARY, borderRadius: "6px" }}
                onClick={() => {
                  setShowAddTimeSheet(true);
                  setSelectedWeek(null);
                  setDuplicateWeek(null);
                  setDuplicateTimesheet(false);
                  form.resetFields();
                }}
              // type="primary"
              >
                + Add New Timesheet
              </Button>
            </span>
          </div>

          <TimeSheetHistory handleClickTimeSheet={handleClickTimeSheet} setShowTimesheet={setShowTimesheet} />
        </div>
      )}

      <StyledModal
        open={showAddTimeSheet}
        onCancel={cancelAddTimeSheet}
        title={"Add Timesheet"}
        width={500}
        footer={null}
        closable={true}
        maskClosable={false}
        centered={true}
        destroyOnClose={true}
      >
        <AddTimesheet
          form={form}
          handleClickTimeSheet={handleClickTimeSheet}
          selectedWeek={selectedWeek}
          setSelectedWeek={setSelectedWeek}
          duplicateWeek={duplicateWeek}
          setDuplicateWeek={setDuplicateWeek}
          onCancel={cancelAddTimeSheet}
        />
      </StyledModal>
    </TimeSheetWrapper>
  );
};

export default TimeSheet;

const AddTimesheet = ({
  form,
  handleClickTimeSheet,
  selectedWeek,
  setSelectedWeek,
  duplicateWeek,
  setDuplicateWeek,
  onCancel,
}) => {
  const userInfo = useSelector(getUserInfo);

  const userList = useSelector(getUserList);

  const currentUser = userList.find((user) => user.userid === userInfo.userid);

  const country = currentUser?.managingoffice?.[0];

  const weekStart = (week) => (week ? week.clone().startOf("isoWeek") : null);

  var weekEnd = (week) => (week ? week.clone().endOf("isoWeek") : null);

  const weekPeriod = selectedWeek
    ? generateTimesheetWeek(weekStart(selectedWeek), weekEnd(selectedWeek))
    : null;

  const period = selectedWeek ? generateNiceWeekFromWeek(weekPeriod) : "-";

  const { getTimesheet } = queryKeys;

  const { data: timesheetDataRes, isLoading: selectedTimesheetLoading } =
    useFetch(
      `${getTimesheet.key}-week:${weekPeriod}`,
      `${getTimesheet.url}?week=${weekPeriod}&employeeId=${userInfo.employer_id}`,
      {
        enabled: !!weekPeriod,
      }
    );

  const duplicateWeekPeriod = duplicateWeek
    ? generateTimesheetWeek(weekStart(duplicateWeek), weekEnd(duplicateWeek))
    : false;

  const targetMonday = selectedWeek ? weekStart(selectedWeek) : null;
  const targetSunday = selectedWeek ? weekEnd(selectedWeek) : null;
  const targetWeekId =
    targetMonday && targetSunday ? findWeekId(targetMonday, targetSunday) : null;

  const { key: resourcePlanningKey, url: resourcePlanningUrl } =
    queryKeys.getResourePlanningList(targetWeekId);

  const { data: targetResourcesDataRes } = useFetch(
    resourcePlanningKey,
    resourcePlanningUrl,
    {
      enabled: !!targetWeekId,
    },
    {
      startDate: targetMonday ? new Date(targetMonday) : null,
      endDate: targetSunday ? new Date(targetSunday) : null,
      employeeIds: [userInfo?.userid],
      engagementIds: [],
      weekId: targetWeekId ? [targetWeekId] : [],
      managingOffice: currentUser?.managingoffice?.[0],
    }
  );

  const {
    data: duplicateTimesheetDataRes,
    isLoading: isDuplicateTimesheetLoading,
  } = useFetch(
    `${getTimesheet.key}-week:${duplicateWeekPeriod}`,
    `${getTimesheet.url}?week=${duplicateWeekPeriod}&employeeId=${userInfo.employer_id}`,
    {
      enabled: !!duplicateWeekPeriod,
    }
  );

  const hasTimesheet = timesheetDataRes?.response?.response?.status || false;

  useEffect(() => {
    if (hasTimesheet) {
      setSelectedWeek(null);
      form.resetFields();
      notification.error({
        message: `Timesheet for week ${period} already exists`,
      });
    }
  }, [hasTimesheet, period, form]);

  const handleSubmit = () => {
    if (selectedWeek !== null && period !== "-") {
      const duplicateTimesheet =
        duplicateTimesheetDataRes?.response?.response?.timeSheet;

      if (duplicateTimesheet) {
        const newWeekStart = weekStart(selectedWeek).format("YYYY-MM-DD");

        const targetMatterIds = new Set(
          (targetResourcesDataRes?.response?.response || []).map(
            (resource) => resource.matterId
          )
        );

        const filteredDuplicate = duplicateTimesheet
          .filter((item) => item?.client !== "ap")
          .filter(
            (item) =>
              item?.client === "nc" || targetMatterIds.has(item?.engagement)
          )
          .map((item) => ({
            ...item,
            status: "draft",
            currentWeek: newWeekStart,
            approval: {},
            declined: {},
          }));

        handleClickTimeSheet(period, filteredDuplicate);
      } else {
        handleClickTimeSheet(period);
      }

      setSelectedWeek(null);
      setDuplicateWeek(null);
    }
  };

  const formatWeekRange = (date) => {
    if (!date) return "";
    const start = date.clone().startOf("isoWeek");
    const end = date.clone().endOf("isoWeek");
    return `${start.format("Do MMM YYYY")} – ${end.format("Do MMM YYYY")}`;
  };

  return (
    <div className="add-timesheet-box">
      <Form
        form={form}
        layout="vertical"
        className="mt-2"
        onFinish={handleSubmit}
      >
        <Spin spinning={selectedTimesheetLoading}>
          <ConfigProvider locale={en_GB}>
            <Form.Item
              label="Choose the period or week"
              name="week"
              rules={[{ required: true, message: "Please select week" }]}
            >
              <DatePicker
                picker="week"
                className="w-100"
                value={selectedWeek}
                format={formatWeekRange}
                onChange={(date) => {
                  setSelectedWeek(date);
                }}
              />
            </Form.Item>
          </ConfigProvider>
        </Spin>

        <ConfigProvider locale={en_GB}>
          <Form.Item label="Duplicate Week" name="duplicateWeek">
            <DatePicker
              picker="week"
              className="w-100"
              value={duplicateWeek}
              format={formatWeekRange}
              onChange={(date) => {
                setDuplicateWeek(date);
              }}
            />
          </Form.Item>
        </ConfigProvider>

        <div className="add-timesheet-actions">
          <Button
            className="cancel-add-button"
            onClick={() => {
              setSelectedWeek(null);
              setDuplicateWeek(null);
              form.resetFields();
              onCancel?.();
            }}
          >
            Cancel
          </Button>
          <Button
            className="add-button"
            htmlType="submit"
            type="primary"
            disabled={selectedTimesheetLoading || isDuplicateTimesheetLoading}
          >
            Add
          </Button>
        </div>
      </Form>
    </div>
  );
};

const TimeSheetWrapper = styled.div`
  .timesheet-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .left-icon {
      width: 46px;
      height: 46px;
      &:hover {
        cursor: pointer;
        opacity: 0.7;
      }
    }
    .right-icon {
      margin-left: -10px;
      width: 46px;
      height: 46px;
      &:hover {
        cursor: pointer;
        opacity: 0.7;
      }
    }
  }
  .date {
    font-weight: 600;
  }
  .copy-icon {
    cursor: pointer;
    margin-left: -4px;
  }
  .submit-button,
  .history-button,
  .add-more,
  .close-button {
    border-radius: 5px;
    margin-bottom: 4px;
    margin-left: 0px;
  }
  .duplicate-week {
    font-size: 14px;
    font-weight: 500;
  }
  .no-timesheet {
    text-align: center;
    margin-top: 1rem;
    color: #bfbfbf;
    font-weight: 600;
    font-size: 1.2rem;
  }
  .current-week {
    background: #f2f6ff;
    padding: 4px 8px;
    border-radius: 5px;
    cursor: pointer;
    &:hover {
      background: #e6f0ff;
    }
  }
  .timesheet-sticky-breadcrumb {
    position: sticky;
    top: 0;
    ${'' /* z-index: 21; */}
    background: #ffffff;
    margin: -10px -24px 0;
    padding: 0 24px 8px;
    will-change: transform;
    transform: translateZ(0);
  }
  .ant-breadcrumb {
    font-size: 16px;
  }
  .ant-breadcrumb-link {
    cursor: pointer;
    color: #636980;
  }
  .ant-breadcrumb-separator {
    color: #636980;
  }
  .ant-breadcrumb > span:last-child .ant-breadcrumb-link {
    cursor: default;
    color: #1f243b;
  }
  .heading {
    font-size: 18px;
    font-weight: 500;
    margin-bottom: 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    .btn-timesheet{
    color: #fff!important;
    }
  }

  button.ant-btn {
    border-radius: 5px;
  }
  .ant-spin-container > div > div  {
    top:-23px!important;
}
`;

const StyledModal = styled(Modal)`
  .ant-modal-close-x {
    .anticon-close.ant-modal-close-icon,
    svg {
      width: 20px;
      height: 20px;
    }
  }
  .ant-modal-header {
    border-bottom: none;
    padding-top: 10px;
  }
  .ant-modal-title {
    font-weight: 500;
    text-align: left;
    color: #1b1b1b;
  }
  .ant-modal-content {
    border-radius: 10px;
  }
  .anticon {
    color: #000000;
    width: 14px;
    height: 14px;
  }
  .ant-modal-body {
    padding-top: 0px;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-btn-text:focus,
  .ant-btn-text:hover {
    background: #26a4ff;
    color: #ffffff;
  }
  .ant-checkbox.ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #02b209 !important;
      border-color: #02b209 !important;
    }
  }
  .ant-upload-span {
    cursor: pointer;
  }
  .add-timesheet-box {
    padding: 20px;
    border: 1px solid #e8e8e8;
    .add-button{
      background: #00685F!important;
      color: #ffffff!important;
      border: none!important;
    }
    .add-timesheet-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 10px;
    }
  }
`;